import {useState} from 'react'
import {Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {FAMILY_DECLINE_CAUSES} from '#/lib/familyCopy'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Text} from '#/components/Typography'

/**
 * Decline-with-a-reason picker for a received family invitation. The causes
 * are honest, non-blaming (\"sent by mistake\", not \"spam\") so a decline still
 * feels respectful, plus an optional personal note. Confirming replies to the
 * sender with a polite message — exactly like accepting does.
 */
export function FamilyDeclineDialog({
  control,
  peerName,
  isPending,
  onConfirm,
}: {
  control: Dialog.DialogControlProps
  peerName?: string
  isPending?: boolean
  onConfirm: (cause: string, note?: string) => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const [cause, setCause] = useState<string>(FAMILY_DECLINE_CAUSES[0])
  const [note, setNote] = useState('')

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={_(msg`Decline family invitation`)}>
        <View style={[a.gap_lg]}>
          <View style={[a.gap_xs]}>
            <Text style={[a.text_2xl, a.font_bold]}>
              <Trans>Decline this invitation?</Trans>
            </Text>
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              {peerName ? (
                <Trans>
                  Let {peerName} know you can't accept right now. Pick a reason —
                  it's sent back to them as a kind message.
                </Trans>
              ) : (
                <Trans>
                  Pick a reason — it's sent back to the sender as a kind
                  message.
                </Trans>
              )}
            </Text>
          </View>

          <View style={[a.gap_sm]}>
            {FAMILY_DECLINE_CAUSES.map(c => {
              const selected = cause === c
              return (
                <Pressable
                  key={c}
                  accessibilityRole="radio"
                  accessibilityState={{selected}}
                  accessibilityLabel={c}
                  accessibilityHint={_(msg`Selects this as the reason`)}
                  onPress={() => setCause(c)}
                  style={({pressed}) => [
                    a.flex_row,
                    a.align_center,
                    a.gap_md,
                    a.border,
                    a.rounded_md,
                    a.px_md,
                    a.py_sm,
                    {
                      borderColor: selected
                        ? t.palette.primary_500
                        : t.atoms.border_contrast_low.borderColor,
                      backgroundColor: selected
                        ? t.palette.primary_50
                        : 'transparent',
                      minHeight: 44,
                    },
                    pressed && t.atoms.bg_contrast_25,
                  ]}>
                  <View
                    style={[
                      {
                        width: 20,
                        height: 20,
                        borderRadius: 10,
                        borderWidth: 2,
                        borderColor: selected
                          ? t.palette.primary_500
                          : t.atoms.border_contrast_high.borderColor,
                      },
                      a.align_center,
                      a.justify_center,
                    ]}>
                    {selected ? (
                      <View
                        style={[
                          {
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: t.palette.primary_500,
                          },
                        ]}
                      />
                    ) : null}
                  </View>
                  <Text style={[a.text_md, a.flex_1]}>{c}</Text>
                </Pressable>
              )
            })}
          </View>

          <View style={[a.gap_sm]}>
            <TextField.LabelText>
              <Trans>Add a personal note (optional)</Trans>
            </TextField.LabelText>
            <TextField.Root>
              <TextField.Input
                label={_(msg`Personal note`)}
                placeholder={_(
                  msg`e.g. Maybe another time — but thank you for thinking of me`,
                )}
                autoCorrect
                multiline
                value={note}
                onChangeText={setNote}
              />
            </TextField.Root>
          </View>

          <View style={[a.flex_row, a.gap_sm, a.justify_end]}>
            <Button
              label={_(msg`Cancel`)}
              size="small"
              color="secondary"
              onPress={() => control.close()}>
              <ButtonText>
                <Trans>Cancel</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Send decline`)}
              size="small"
              color="primary"
              disabled={isPending}
              onPress={() => {
                if (!cause) return
                onConfirm(cause, note.trim() || undefined)
                control.close()
              }}>
              <ButtonText>
                <Trans>Send decline</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
